import { FC, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';

import { TFolder, useFolder } from '@modules/items';
import { useSubmission } from '@modules/submissions/api';
import { TSingleSubmissionData } from '@modules/submissions/types/submissions.types';
import { StatusLabel } from '@shared/components';
import { useDocumentTitle } from '@shared/hooks';

import { getComplianceCheckConfigFromSearchParams } from '../constants';
import ComplianceCheckResultContent from '../components/ComplianceCheckResultContent';

const ComplianceCheckResultPage: FC = () => {
  const { productId, submissionId } = useParams();
  const [searchParams] = useSearchParams();

  const { error: folderError, data: folder } = useFolder(productId);
  const { error: submissionError, data: submissionData } = useSubmission(submissionId as string);

  const error = folderError || submissionError;
  const config = getComplianceCheckConfigFromSearchParams(searchParams);

  const data: TSingleSubmissionData | null = useMemo(() => {
    if (folder && submissionData) {
      return {
        folder: folder as TFolder,
        submissionData,
      };
    }

    return null;
  }, [folder, submissionData]);

  useDocumentTitle(submissionData ? `Compliance check | ${submissionData.submission.title}` : 'Compliance check');

  return (
    <StatusLabel
      data={data}
      loadingMessage="Loading compliance check..."
      error={error}
      errorMessage="Loading compliance check failed!"
      render={(resolvedData) => <ComplianceCheckResultContent data={resolvedData} config={config} />}
    />
  );
};

export default ComplianceCheckResultPage;
